import { Capacitor, registerPlugin } from '@capacitor/core'
import { log } from './logger'
import type { HourCycle } from './timeFormat'
import { weekStartFromFirstWeekday, type WeekStartsOn } from './eventCalendar'

// Local plugin (ClockFormatPlugin on iOS and Android) reporting the phone's
// 12/24-hour setting and first day of the week, which the WebView's Intl doesn't
// reflect. Absent on the web and on shells older than the plugin, and
// `firstWeekday` is absent on shells that predate it — callers fall back to the
// app language.
const ClockFormat = registerPlugin<{ get(): Promise<{ is24Hour: boolean; firstWeekday?: number }> }>('ClockFormat')

const CACHE_KEY = 'beacon_device_clock'
const WEEK_START_CACHE_KEY = 'beacon_device_week_start'

export const hasDeviceClock = () => Capacitor.isPluginAvailable('ClockFormat')

function cached(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Re-read next boot instead.
  }
}

/** The last setting read, so a cold boot applies it before the plugin answers. */
export function cachedDeviceClock(): HourCycle | null {
  const raw = cached(CACHE_KEY)
  return raw === '12h' || raw === '24h' ? raw : null
}

export function cachedDeviceWeekStart(): WeekStartsOn | null {
  const raw = cached(WEEK_START_CACHE_KEY)
  return raw === '0' ? 0 : raw === '1' ? 1 : null
}

type DeviceSettings = { hourCycle: HourCycle; weekStartsOn: WeekStartsOn | null }

// The clock and week-start hooks both refresh on mount and on every resume;
// sharing the in-flight read keeps that to one native call.
let inFlight: Promise<DeviceSettings | null> | null = null

function readDeviceSettings(): Promise<DeviceSettings | null> {
  inFlight ??= ClockFormat.get()
    .then(({ is24Hour, firstWeekday }) => {
      const hourCycle: HourCycle = is24Hour ? '24h' : '12h'
      const weekStartsOn = weekStartFromFirstWeekday(firstWeekday)
      remember(CACHE_KEY, hourCycle)
      if (weekStartsOn !== null) remember(WEEK_START_CACHE_KEY, String(weekStartsOn))
      return { hourCycle, weekStartsOn }
    })
    .catch((err: unknown) => {
      log('time-format', 'device clock read failed', { message: err instanceof Error ? err.message : String(err) })
      return null
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

export async function readDeviceClock(): Promise<HourCycle | null> {
  return (await readDeviceSettings())?.hourCycle ?? null
}

export async function readDeviceWeekStart(): Promise<WeekStartsOn | null> {
  return (await readDeviceSettings())?.weekStartsOn ?? null
}
